import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {connect} from 'react-redux';
import {resetReportWidgetsLoadingState} from '../../../../../../services/redux/actions';
import phantom from '../../../../../../services/phantom';
import ReactGridLayout from 'react-grid-layout';
import Paper from 'material-ui/Paper';
import ExportCommentsWidget from './components/ExportCommentsWidget';
import withQueryStringProps from '../../../../../../components/withQueryStringProps';
import WidgetTypeRouter from '../../../../../../components/WidgetTypeRouter';
import _ from 'lodash';

function getWidgetHeight(layoutItem, rowHeight, rowMargin) {
  const itemHeight = layoutItem ? layoutItem.h : 1; 
  return (itemHeight*(rowHeight+rowMargin))-rowMargin;
}

function getWidgetWidth(layoutItem, gridWidth, columns, columnMargin) {
  const itemWidth = layoutItem ? layoutItem.w : 1
  const singleColumnWidth = (gridWidth-(columnMargin*(columns-1)))/columns;
  return (itemWidth*(singleColumnWidth+columnMargin))-columnMargin;
}

function getLayoutPages(layout = [], widgets = []) {
  const sortedLayout = _.sortBy(layout, ['y', 'x']);
  const pageBreakYPositions = widgets.reduce(
    (positions, widget) => {
      if (widget.type === 'page-break') {
        const layoutItem = _.find(sortedLayout, (l) => l.i === widget.id);
        return [...positions, layoutItem.y].sort();
      } else {
        return positions;
      }
    },
    []
  );
  return sortedLayout.reduce(
    (pages, layoutItem) => {
      if (_.includes(pageBreakYPositions, layoutItem.y)) {
        return pages;
      } else {
        const layoutIndex = _.sortedIndex(pageBreakYPositions, layoutItem.y);
        const yOffset = layoutIndex === 0 ? 0 : pageBreakYPositions[layoutIndex-1] + 3;
        return pages.map((page, i) => {
          if (layoutIndex === i) {
            return [
              ...page,
              Object.assign({}, layoutItem, {y: layoutItem.y - yOffset}),
            ];
          } else {
            return page;
          }
        });
      }
    },
    _.fill(Array(pageBreakYPositions.length + 1), [])
  );
}

const gridDimensions = {
  width: 1200,
  columns: 12,
  columnMargin: 20,
  rowHeight: 10,
  rowMargin: 20,
};

const styles = {
  paper: {
    margin: 0,
    textAlign: 'center',
    display: 'inline-block',
    overflow: 'hidden',
  },
  commentPaperWrapper: {
    pageBreakBefore: 'always',
    padding: '60px 0px',
    marginBottom: '20px',
  },
  commentPaper: {
    margin: 0,
    position: 'relative',
    boxShadow: '0px 0px 0px 1px #ccc',
  },
};

@connect()
@withQueryStringProps(['print'])
class UserReportGrid extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pages: getLayoutPages(props.report.layout, props.report.widgets),
    };
  }

  componentDidMount() {
    // Let phantom know when report is loaded
    let widgetIds = this.props.report.widgets
      .filter(widget => widget.type !== 'page-break')
      .map(widget => widget.id);
    
    if (this.props.print && this.state.pages.length > 1) {
      const commentWidgetIds = this.props.report.widgets
        .filter(widget => widget.type === 'comments')
        .map(widget => widget.id + '-print');
      widgetIds = [...widgetIds, ...commentWidgetIds];
    }

    if (widgetIds.length > 0) {
      phantom.reportLoading();
      this.props.dispatch(
        resetReportWidgetsLoadingState(
          this.props.report.id,
          widgetIds,
        ),
      );
    } else {
      phantom.reportLoaded();
    }

    // Tell phantom if report has several pages
    window.tntReportPageCount = this.state.pages.length;
  }

  render() {
    const { report, reports, surveys } = this.props;

    return (
      <div>
        {this.state.pages.map((layout, i) => {
          const widgets = report.widgets.filter(widget => _.find(layout, l => l.i === widget.id));
          let gridWrapperStyles = {
            marginBottom: '20px',
            pageBreakBefore: i !== 0 && widgets.length > 0 ? 'always' : 'avoid',
            padding: i !== 0 && this.props.print ? '60px 0px' : 0,
          };
          return (
            <div key={i} style={gridWrapperStyles}>
              <ReactGridLayout 
                className="layout"
                layout={layout}
                cols={gridDimensions.columns}
                margin={[
                  gridDimensions.columnMargin,
                  gridDimensions.rowMargin,
                ]}
                containerPadding={[0,0]}
                rowHeight={gridDimensions.rowHeight}
                width={gridDimensions.width}
                isDraggable={false}
                isResizable={false}
              >
                {widgets.map((widget) => {
                  const layoutItem = layout.find((l) => l.i === widget.id);
                  const height = getWidgetHeight(
                    layoutItem,
                    gridDimensions.rowHeight,
                    gridDimensions.rowMargin,
                  );
                  const width = getWidgetWidth(
                    layoutItem,
                    gridDimensions.width,
                    gridDimensions.columns,
                    gridDimensions.columnMargin,
                  );
                  return (
                    <Paper
                      key={widget.id}
                      style={
                        this.props.print
                          ? Object.assign({}, styles.paper, {boxShadow: '0px 0px 0px 1px #ccc'})
                          : styles.paper
                      }
                      zDepth={0}
                    >
                      <WidgetTypeRouter 
                        widget={widget}
                        report={report}
                        reports={reports}
                        surveys={surveys}
                        height={height}
                        width={width}
                        isAccountAdmin={_.includes(this.props.viewer.roles, 'account_admin')}
                      />
                    </Paper>
                  );
                })}
              </ReactGridLayout>
            </div>
          );
        })}
        {this.props.print && 
          this.state.pages.length > 1 && 
          report.widgets.filter(w => w.type === 'comments').map(widget =>
            <div key={widget.id} style={styles.commentPaperWrapper}>
              <Paper style={styles.commentPaper} zDepth={0}>
                <ExportCommentsWidget
                  widget={widget}
                  report={report}
                  surveys={surveys}
                  height={'auto'}
                  width={1200}
                  isAccountAdmin={_.includes(this.props.viewer.roles, 'account_admin')}
                />
              </Paper>
            </div>
        )}
      </div>
    )
  }
}

export default createFragmentContainer(
  UserReportGrid,
  graphql`
    fragment UserReportGrid_viewer on Viewer {
      roles
    }
    fragment UserReportGrid_report on Report {
      id
      layout {
        i
        x
        y
        w
        h
      }
      widgets {
        id
        type
        ...WidgetTypeRouter_widget
        ...ExportCommentsWidget_widget
      }
      ...WidgetTypeRouter_report
      ...ExportCommentsWidget_report
    }
    fragment UserReportGrid_surveys on SurveyConnection {
      ...WidgetTypeRouter_surveys
      ...ExportCommentsWidget_surveys
    }
    fragment UserReportGrid_reports on ReportConnection {
      ...WidgetTypeRouter_reports
    }
  `,
);



// WEBPACK FOOTER //
// ./src/scenes/Report/components/ReportWrapper/components/UserReportGrid/UserReportGrid.js